import AddIcon from '@mui/icons-material/Add'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye'
import SearchIcon from '@mui/icons-material/Search'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import baseUrl from '../../api/baseUrl'
import LabOrderDetailModal from '../../components/labs/LabOrderDetailModal'
import { LabStatusBadge, displayDate } from '../../components/labs/LabOrderDetailView'
import { useClinicDentists } from '../../components/labs/useClinicDentists'
import { useLabCatalog } from '../../components/labs/useLabCatalog'
import {
  URGENT_BADGE,
  caseLabel,
  formatLkr,
  orderMatchesKey,
  orderStatusStyles,
  paymentStatusStyles,
  resolveOrderKey,
  type LabOrderDetail,
  type LabOrderListItem,
} from '../../components/labs/labOrderTypes'
import FilterChecklist from './FilterChecklist'
import {
  PAGE_SIZES,
  PAYMENTS,
  STATUSES,
  type OrdersFilters,
  type SortKey,
  apiParams,
  currentMonth,
  filtersFromSearch,
  filtersToSearch,
  monthLabel,
  nextSort,
  shiftMonth,
} from './labOrdersQuery'

/** A row on the Lab Orders page (list fields plus the Accounts columns). */
type OrderRow = LabOrderListItem & {
  patientPhone?: string
  currentStage?: string
  units?: number
  unitPrice?: number | null
  caseType?: string
  paidDate?: string
  finalTotalAmount?: number
  totalReceivedAmount?: number
}

type Summary = {
  totalOrders: number
  inProgress: number
  awaitingPayment: number
  balanceDue: number
  price: number
  paid: number
}

const EMPTY_SUMMARY: Summary = { totalOrders: 0, inProgress: 0, awaitingPayment: 0, balanceDue: 0, price: 0, paid: 0 }
const PAID_LABEL: Record<string, string> = { Settled: 'Yes', Partial: 'Partial', Unpaid: 'No' }
const fieldClass =
  'h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm dark:border-slate-600 dark:bg-slate-800'
const th = 'px-3 py-3 font-medium text-slate-500 whitespace-nowrap'
const td = 'px-3 py-2.5 align-top'

function errorText(err: unknown, fallback: string) {
  return (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback
}

const today = () => new Date().toISOString().slice(0, 10)
const isOverdue = (o: OrderRow) =>
  !!o.dueDate && /^\d{4}-\d{2}-\d{2}$/.test(o.dueDate) && o.dueDate < today() && ['Pending', 'Accepted', 'In Progress'].includes(o.orderStatus)

/**
 * Clinic Lab Orders (requirement 11): the CDCI SUMMARY and BILL SUMMARY
 * sheets as two tabs over the same filtered, paginated list. Filters live in
 * the URL; the stat cards follow the filter.
 */
function ClinicLabOrders() {
  const [searchParams, setSearchParams] = useSearchParams()
  const filters = useMemo(() => filtersFromSearch(searchParams), [searchParams])
  const [rows, setRows] = useState<OrderRow[]>([])
  const [total, setTotal] = useState(0)
  const [summary, setSummary] = useState<Summary>(EMPTY_SUMMARY)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [exporting, setExporting] = useState(false)
  const [searchDraft, setSearchDraft] = useState(filters.q)
  const [partners, setPartners] = useState<{ id: string; name: string }[]>([])
  const { catalog } = useLabCatalog()
  const dentists = useClinicDentists()
  const request = useRef(0)

  const [modalOrderKey, setModalOrderKey] = useState<string | null>(null)
  const [modalListOrder, setModalListOrder] = useState<LabOrderListItem | null>(null)
  const [orderDetail, setOrderDetail] = useState<LabOrderDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const detailRequest = useRef(0)

  const setFilters = useCallback(
    (patch: Partial<OrdersFilters>, { keepPage = false } = {}) => {
      const next = { ...filters, ...patch, page: keepPage ? patch.page ?? filters.page : patch.page ?? 1 }
      setSearchParams(filtersToSearch(next), { replace: false })
    },
    [filters, setSearchParams],
  )

  const load = useCallback(() => {
    const id = ++request.current
    setLoading(true)
    setError('')
    baseUrl
      .get('client/labs/orders', { params: apiParams(filters) })
      .then((res) => {
        if (id !== request.current) return
        setRows(res.data.data ?? [])
        setTotal(Number(res.data.total) || 0)
        setSummary({ ...EMPTY_SUMMARY, ...(res.data.summary || {}) })
      })
      .catch((err) => {
        if (id !== request.current) return
        setRows([])
        setTotal(0)
        setSummary(EMPTY_SUMMARY)
        setError(errorText(err, 'Could not load lab orders'))
      })
      .finally(() => {
        if (id === request.current) setLoading(false)
      })
  }, [filters])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    setSearchDraft(filters.q)
  }, [filters.q])

  // Search waits until typing stops.
  useEffect(() => {
    if (searchDraft.trim() === filters.q.trim()) return
    const t = window.setTimeout(() => setFilters({ q: searchDraft }), 400)
    return () => window.clearTimeout(t)
  }, [searchDraft, filters.q, setFilters])

  useEffect(() => {
    baseUrl
      .get('client/labs/partners')
      .then((res) => setPartners((res.data.data ?? []).map((p: { id: string; name: string }) => ({ id: String(p.id), name: p.name }))))
      .catch(() => setPartners([]))
  }, [])

  useEffect(() => {
    if (!modalOrderKey) {
      setOrderDetail(null)
      return
    }
    const id = ++detailRequest.current
    const controller = new AbortController()
    setDetailLoading(true)
    setOrderDetail(null)
    baseUrl
      .get(`client/labs/orders/${encodeURIComponent(modalOrderKey)}`, { signal: controller.signal })
      .then((res) => {
        if (controller.signal.aborted || id !== detailRequest.current) return
        setOrderDetail((res.data.data ?? null) as LabOrderDetail | null)
      })
      .catch((err: { code?: string; name?: string }) => {
        if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return
        setOrderDetail(null)
      })
      .finally(() => {
        if (!controller.signal.aborted) setDetailLoading(false)
      })
    return () => controller.abort()
  }, [modalOrderKey])

  const categoryOptions = useMemo(() => catalog.map((c) => ({ id: c.id, label: c.name })), [catalog])
  const caseTypeOptions = useMemo(
    () =>
      catalog
        .filter((c) => !filters.categoryIds.length || filters.categoryIds.includes(c.id))
        .flatMap((c) => c.items.map((i) => ({ id: i.id, label: i.name, group: c.name }))),
    [catalog, filters.categoryIds],
  )

  async function patchOrder(row: OrderRow, body: Record<string, unknown>, done: string) {
    try {
      await baseUrl.patch(`client/labs/orders/${encodeURIComponent(row._id)}`, body)
      toast.success(done)
      load()
    } catch (err) {
      toast.error(errorText(err, 'Could not save'))
    }
  }

  async function exportExcel() {
    setExporting(true)
    try {
      const { page: _page, limit: _limit, ...params } = apiParams(filters)
      const res = await baseUrl.get('client/labs/orders/export.xlsx', { params, responseType: 'blob' })
      const href = URL.createObjectURL(res.data as Blob)
      const a = document.createElement('a')
      a.href = href
      a.download = `lab-orders-${filters.tab}${filters.month !== 'all' ? `-${filters.month}` : ''}.xlsx`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(href)
    } catch (err) {
      toast.error(errorText(err, 'Could not export'))
    } finally {
      setExporting(false)
    }
  }

  function openOrder(o: OrderRow) {
    const key = resolveOrderKey(o)
    if (!key) return
    setModalListOrder(o)
    setOrderDetail(null)
    setModalOrderKey(key)
  }

  const modalOrder = orderDetail && orderMatchesKey(orderDetail, modalOrderKey) ? orderDetail : null
  const isAccounts = filters.tab === 'accounts'
  const from = total === 0 ? 0 : (filters.page - 1) * filters.limit + 1
  const to = Math.min(total, filters.page * filters.limit)
  const pages = Math.max(1, Math.ceil(total / filters.limit))

  const sortHeader = (key: SortKey, label: string, className = '') => {
    const active = filters.sort.replace('-', '') === key
    const desc = filters.sort.startsWith('-')
    return (
      <th className={`${th} ${className}`} aria-sort={active ? (desc ? 'descending' : 'ascending') : 'none'}>
        <button type="button" onClick={() => setFilters({ sort: nextSort(filters.sort, key) })} className="inline-flex items-center gap-1 hover:text-slate-800">
          {label}
          <span aria-hidden className={active ? 'text-primary' : 'text-slate-300'}>
            {active ? (desc ? '↓' : '↑') : '↕'}
          </span>
        </button>
      </th>
    )
  }

  return (
    <div className="flex min-h-[calc(100dvh-10rem)] w-full flex-col gap-5 p-4 md:p-6 lg:p-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100 md:text-xl">Lab orders</h1>
          <p className="mt-1 text-sm text-slate-500">Orders sent to partner labs and personal labs you track yourself.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={exportExcel}
            disabled={exporting || total === 0}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
          >
            <FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />
            {exporting ? 'Exporting…' : 'Export to Excel'}
          </button>
          <Link
            to="/client/labs/send-order"
            className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-primary to-primaryLight px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-95"
          >
            <AddIcon sx={{ fontSize: 18 }} />
            Send new order
          </Link>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { label: 'Orders', value: String(summary.totalOrders), tone: 'text-slate-900 dark:text-slate-100' },
          { label: 'In progress', value: String(summary.inProgress), tone: 'text-amber-600' },
          { label: 'Awaiting payment', value: String(summary.awaitingPayment), tone: 'text-red-600' },
          { label: 'Balance due', value: formatLkr(summary.balanceDue), tone: 'text-primary' },
        ].map((c) => (
          <div key={c.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{c.label}</p>
            <p className={`mt-1 text-2xl font-bold tabular-nums ${c.tone}`}>{c.value}</p>
            <p className="mt-0.5 text-xs text-slate-400">{monthLabel(filters.month)} · current filters</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-slate-200 dark:border-slate-700">
        <div className="flex gap-1" role="tablist" aria-label="Lab orders view">
          {(['cases', 'accounts'] as const).map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={filters.tab === t}
              onClick={() => setFilters({ tab: t }, { keepPage: true })}
              className={`-mb-px border-b-2 px-4 py-2.5 text-sm font-medium ${
                filters.tab === t ? 'border-primary text-primary' : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {t === 'cases' ? 'Cases' : 'Accounts'}
            </button>
          ))}
        </div>
        <div className="mb-2 flex items-center gap-1">
          <button
            type="button"
            onClick={() => setFilters({ month: shiftMonth(filters.month === 'all' ? currentMonth() : filters.month, -1) })}
            className="flex size-9 items-center justify-center rounded-lg border border-slate-300 dark:border-slate-600"
            aria-label="Previous month"
          >
            <ChevronLeftIcon sx={{ fontSize: 20 }} />
          </button>
          <span className="min-w-[9rem] text-center text-sm font-semibold text-slate-800 dark:text-slate-100">{monthLabel(filters.month)}</span>
          <button
            type="button"
            onClick={() => setFilters({ month: shiftMonth(filters.month === 'all' ? currentMonth() : filters.month, 1) })}
            className="flex size-9 items-center justify-center rounded-lg border border-slate-300 dark:border-slate-600"
            aria-label="Next month"
          >
            <ChevronRightIcon sx={{ fontSize: 20 }} />
          </button>
          <button
            type="button"
            onClick={() => setFilters({ month: filters.month === 'all' ? currentMonth() : 'all' })}
            className={`ml-1 h-9 rounded-lg border px-3 text-sm ${filters.month === 'all' ? 'border-primary bg-primary/5 text-primary' : 'border-slate-300 text-slate-600 dark:border-slate-600'}`}
          >
            All time
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1 sm:max-w-sm">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" sx={{ fontSize: 20 }} />
          <input
            type="search"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            placeholder="Case no, lab #, patient, phone, lab…"
            aria-label="Search lab orders"
            className={`${fieldClass} w-full pl-10`}
          />
        </div>
        <FilterChecklist
          label="Category"
          options={categoryOptions}
          selected={filters.categoryIds}
          onChange={(categoryIds) => {
            const allowed = new Set(
              catalog.filter((c) => !categoryIds.length || categoryIds.includes(c.id)).flatMap((c) => c.items.map((i) => i.id)),
            )
            setFilters({ categoryIds, catalogItemIds: filters.catalogItemIds.filter((id) => allowed.has(id)) })
          }}
        />
        <FilterChecklist
          label="Case type"
          options={caseTypeOptions}
          selected={filters.catalogItemIds}
          onChange={(catalogItemIds) => setFilters({ catalogItemIds })}
          searchable
        />
        <select
          value={filters.labPartnerId}
          onChange={(e) => setFilters({ labPartnerId: e.target.value })}
          className={fieldClass}
          aria-label="Lab"
        >
          <option value="">All labs</option>
          {partners.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <FilterChecklist
          label="Status"
          options={STATUSES.map((s) => ({ id: s, label: s }))}
          selected={filters.status}
          onChange={(status) => setFilters({ status })}
        />
        {isAccounts ? (
          <FilterChecklist
            label="Paid"
            options={PAYMENTS.map((p) => ({ id: p, label: PAID_LABEL[p] }))}
            selected={filters.payment}
            onChange={(payment) => setFilters({ payment })}
          />
        ) : null}
        <select
          value={filters.dentistId}
          onChange={(e) => setFilters({ dentistId: e.target.value })}
          className={fieldClass}
          aria-label="Doctor"
        >
          <option value="">All doctors</option>
          {dentists.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>
        <label className={`inline-flex h-10 cursor-pointer items-center gap-2 rounded-lg border px-3 text-sm ${filters.urgent ? 'border-red-400 bg-red-50 text-red-700' : 'border-slate-300 text-slate-700 dark:border-slate-600 dark:text-slate-200'}`}>
          <input type="checkbox" checked={filters.urgent} onChange={(e) => setFilters({ urgent: e.target.checked })} className="size-4" />
          Urgent only
        </label>
      </div>

      {error ? (
        <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </p>
      ) : null}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1100px] text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/80 dark:border-slate-700 dark:bg-slate-800/80">
                {isAccounts ? (
                  <>
                    {sortHeader('orderDate', 'Date')}
                    <th className={th}>Case no</th>
                    <th className={th}>Patient</th>
                    <th className={th}>Doctor</th>
                    <th className={th}>Case type</th>
                    {sortHeader('lab', 'Lab')}
                    {sortHeader('dueDate', 'Need date')}
                    {sortHeader('status', 'Status')}
                    <th className={`${th} text-right`}>Units</th>
                    <th className={`${th} text-right`}>Unit price</th>
                    {sortHeader('price', 'Price', 'text-right')}
                    <th className={th}>Lab invoice no</th>
                    <th className={th}>Paid</th>
                    <th className={th}>Paid date</th>
                  </>
                ) : (
                  <>
                    <th className={th}>#</th>
                    <th className={th}>Case no</th>
                    {sortHeader('orderDate', 'Date')}
                    <th className={th}>Patient</th>
                    <th className={th}>Doctor</th>
                    <th className={th}>Case type</th>
                    {sortHeader('lab', 'Lab')}
                    {sortHeader('dueDate', 'Need date')}
                    {sortHeader('status', 'Status')}
                    <th className={th}>Stage</th>
                    <th className={th}>Informed</th>
                    <th className={th}>Modelled by</th>
                  </>
                )}
                <th className={`${th} text-right`}>
                  <span className="sr-only">View</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={15} className="px-4 py-12 text-center text-slate-500">
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={15} className="px-4 py-12 text-center text-slate-500">
                    No lab orders match these filters.
                  </td>
                </tr>
              ) : (
                rows.map((o, i) => {
                  const caseCell = (
                    <td className={td}>
                      <span className="font-medium text-primary">{caseLabel(o)}</span>
                      {o.clinicCaseId && o.labOrderNumber ? <span className="block text-xs text-slate-500">Lab #{o.labOrderNumber}</span> : null}
                      {o.priority === 'Urgent' ? (
                        <span className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${URGENT_BADGE}`}>Urgent</span>
                      ) : null}
                    </td>
                  )
                  const patientCell = (
                    <td className={td}>
                      <span className="text-slate-900 dark:text-slate-100">{o.patient || '—'}</span>
                      {o.patientPhone ? <span className="block text-xs text-slate-500">{o.patientPhone}</span> : null}
                    </td>
                  )
                  const statusCell = (
                    <td className={td}>
                      <LabStatusBadge label={o.orderStatus} className={orderStatusStyles[o.orderStatus]} />
                    </td>
                  )
                  const dueCell = (
                    <td className={`${td} whitespace-nowrap ${isOverdue(o) ? 'font-medium text-red-600' : 'text-slate-700 dark:text-slate-300'}`}>
                      {o.dueDate ? displayDate(o.dueDate) : '—'}
                    </td>
                  )
                  const view = (
                    <td className={`${td} text-right`}>
                      <button
                        type="button"
                        onClick={() => openOrder(o)}
                        className="inline-flex size-8 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 hover:bg-indigo-100 dark:bg-indigo-900/40 dark:text-indigo-300"
                        aria-label={`View ${caseLabel(o)}`}
                      >
                        <RemoveRedEyeIcon sx={{ fontSize: 18 }} />
                      </button>
                    </td>
                  )
                  return (
                    <tr key={resolveOrderKey(o)} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/80 dark:border-slate-800 dark:hover:bg-slate-800/50">
                      {isAccounts ? (
                        <>
                          <td className={`${td} whitespace-nowrap`}>{o.orderDate || '—'}</td>
                          {caseCell}
                          {patientCell}
                          <td className={td}>{o.dentistName || '—'}</td>
                          <td className={`${td} max-w-[200px]`}>{o.caseType || o.case}</td>
                          <td className={td}>{o.labName}</td>
                          {dueCell}
                          {statusCell}
                          <td className={`${td} text-right tabular-nums`}>{o.units ?? '—'}</td>
                          <td className={`${td} text-right tabular-nums`}>{o.unitPrice != null ? formatLkr(o.unitPrice) : '—'}</td>
                          <td className={`${td} text-right font-medium tabular-nums`}>{o.pricePending ? 'Not set' : o.finalTotal}</td>
                          <td className={td}>
                            <input
                              defaultValue={o.labInvoiceNo || ''}
                              key={`${o._id}-${o.labInvoiceNo || ''}`}
                              aria-label={`Lab invoice number for ${caseLabel(o)}`}
                              maxLength={40}
                              placeholder="—"
                              onBlur={(e) => {
                                const v = e.target.value.trim()
                                if (v !== (o.labInvoiceNo || '')) patchOrder(o, { labInvoiceNo: v }, 'Lab invoice number saved')
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
                              }}
                              className="h-8 w-28 rounded border border-slate-200 px-2 text-sm dark:border-slate-600 dark:bg-slate-900"
                            />
                          </td>
                          <td className={td}>
                            <LabStatusBadge label={PAID_LABEL[o.payment] || o.payment} className={paymentStatusStyles[o.payment]} />
                          </td>
                          <td className={`${td} whitespace-nowrap`}>{o.paidDate || '—'}</td>
                        </>
                      ) : (
                        <>
                          <td className={`${td} text-slate-500`}>{from + i}</td>
                          {caseCell}
                          <td className={`${td} whitespace-nowrap`}>{o.orderDate || '—'}</td>
                          {patientCell}
                          <td className={td}>{o.dentistName || '—'}</td>
                          <td className={`${td} max-w-[200px]`}>{o.caseType || o.case}</td>
                          <td className={td}>
                            {o.labName}
                            {o.isLocalTracking || o.trackingMode === 'local' ? <span className="block text-xs text-slate-500">Self-tracked</span> : null}
                          </td>
                          {dueCell}
                          {statusCell}
                          <td className={`${td} text-slate-700 dark:text-slate-300`}>{o.currentStage || '—'}</td>
                          <td className={td}>
                            <input
                              type="checkbox"
                              checked={!!o.patientInformedAt}
                              aria-label={`Patient informed for ${caseLabel(o)}`}
                              onChange={(e) =>
                                patchOrder(o, { patientInformed: e.target.checked }, e.target.checked ? 'Marked as informed' : 'Unmarked')
                              }
                              className="size-4 rounded border-slate-300"
                            />
                          </td>
                          <td className={td}>{o.modelledByName || o.labName}</td>
                        </>
                      )}
                      {view}
                    </tr>
                  )
                })
              )}
            </tbody>
            {isAccounts && !loading && rows.length ? (
              <tfoot>
                <tr className="border-t-2 border-slate-200 bg-slate-50 font-semibold dark:border-slate-700 dark:bg-slate-800/80">
                  <td className={td} colSpan={10}>
                    Total ({summary.totalOrders} orders, all pages)
                  </td>
                  <td className={`${td} text-right tabular-nums`}>{formatLkr(summary.price)}</td>
                  <td className={td} colSpan={4}>
                    Paid {formatLkr(summary.paid)} · Balance {formatLkr(summary.balanceDue)}
                  </td>
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-4 py-3 text-sm dark:border-slate-700">
          <p className="text-slate-500">
            {total ? `Showing ${from}–${to} of ${total}` : 'No orders'}
          </p>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-slate-500">
              Per page
              <select value={filters.limit} onChange={(e) => setFilters({ limit: Number(e.target.value) })} className="h-9 rounded-lg border border-slate-300 px-2 dark:border-slate-600 dark:bg-slate-800">
                {PAGE_SIZES.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              disabled={filters.page <= 1}
              onClick={() => setFilters({ page: filters.page - 1 }, { keepPage: true })}
              className="h-9 rounded-lg border border-slate-300 px-3 disabled:opacity-40 dark:border-slate-600"
            >
              Previous
            </button>
            <span className="tabular-nums text-slate-600">
              {filters.page} / {pages}
            </span>
            <button
              type="button"
              disabled={filters.page >= pages}
              onClick={() => setFilters({ page: filters.page + 1 }, { keepPage: true })}
              className="h-9 rounded-lg border border-slate-300 px-3 disabled:opacity-40 dark:border-slate-600"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <LabOrderDetailModal
        key={modalOrderKey ?? 'closed'}
        open={modalOrderKey != null}
        orderId={modalOrderKey}
        orderNumber={modalListOrder?.id ?? null}
        listOrder={modalListOrder}
        order={modalOrder}
        loading={detailLoading}
        onClose={() => {
          setModalOrderKey(null)
          setModalListOrder(null)
          setOrderDetail(null)
        }}
        onOrderUpdated={(updated) => {
          if (orderMatchesKey(updated, modalOrderKey)) setOrderDetail(updated)
          load()
        }}
      />
    </div>
  )
}

export default ClinicLabOrders
